import authService from './auth.service';
import type {
  NotificationOut,
  NotificationListParams,
  NotificationListResponse,
  UnreadCountResponse,
  MarkAllReadResponse,
} from '../types/notification.types';

const API_URL = '/api/v1';

class NotificationService {
  async list(params: NotificationListParams = {}): Promise<NotificationListResponse> {
    const q = new URLSearchParams();

    if (params.unread_only !== undefined) q.set('unread_only', String(params.unread_only));
    if (params.source_service !== undefined) q.set('source_service', params.source_service);
    if (params.severity !== undefined) q.set('severity', params.severity);
    if (params.entity_type !== undefined) q.set('entity_type', params.entity_type);
    if (params.entity_id !== undefined) q.set('entity_id', String(params.entity_id));
    if (params.include_dismissed !== undefined) q.set('include_dismissed', String(params.include_dismissed));
    if (params.since !== undefined) q.set('since', params.since);
    if (params.skip !== undefined) q.set('skip', String(params.skip));
    if (params.limit !== undefined) q.set('limit', String(params.limit));

    const qs = q.toString();
    const res = await authService.fetchWithAuth(`${API_URL}/notifications${qs ? `?${qs}` : ''}`);

    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить уведомления');
    }

    return res.json() as Promise<NotificationListResponse>;
  }

  async getById(id: number): Promise<NotificationOut> {
    const res = await authService.fetchWithAuth(`${API_URL}/notifications/${id}`);

    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось получить уведомление');
    }

    return res.json() as Promise<NotificationOut>;
  }

  async getUnreadCount(): Promise<UnreadCountResponse> {
    const res = await authService.fetchWithAuth(`${API_URL}/notifications/unread-count`);

    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось получить количество непрочитанных уведомлений');
    }

    return res.json() as Promise<UnreadCountResponse>;
  }

  async markRead(id: number): Promise<NotificationOut> {
    const res = await authService.fetchWithAuth(`${API_URL}/notifications/${id}:read`, {
      method: 'POST',
    });

    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось отметить уведомление как прочитанное');
    }

    return res.json() as Promise<NotificationOut>;
  }

  async markAllRead(source_service?: string): Promise<MarkAllReadResponse> {
    const body: Record<string, unknown> = {};
    if (source_service !== undefined) {
      body.source_service = source_service;
    }

    const res = await authService.fetchWithAuth(`${API_URL}/notifications:mark-all-read`, {
      method: 'POST',
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось отметить все уведомления как прочитанные');
    }

    return res.json() as Promise<MarkAllReadResponse>;
  }

  async markManyRead(ids: number[]): Promise<MarkAllReadResponse> {
    if (ids.length === 0) {
      return { updated: 0 };
    }

    const results = await Promise.allSettled(ids.map((id) => this.markRead(id)));
    const updated = results.filter((r) => r.status === 'fulfilled').length;

    return { updated };
  }

  async dismiss(id: number): Promise<NotificationOut> {
    const res = await authService.fetchWithAuth(`${API_URL}/notifications/${id}:dismiss`, {
      method: 'POST',
    });

    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось скрыть уведомление');
    }

    return res.json() as Promise<NotificationOut>;
  }

  async dismissMany(ids: number[]): Promise<MarkAllReadResponse> {
    if (ids.length === 0) {
      return { updated: 0 };
    }

    const results = await Promise.allSettled(ids.map((id) => this.dismiss(id)));
    const updated = results.filter((r) => r.status === 'fulfilled').length;

    return { updated };
  }
}

export default new NotificationService();
